import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { Observable } from 'rxjs';
import { SmartintegService } from '../../../../services/smartinteg.service';
import { ToastService } from '../../../../services/toast.service';
import { mensagemErroApi } from '../../../../utils/mensagem-erro-api';
import { DadoDataset, Dataset } from '../../../../types/smartinteg';

@Component({
  selector: 'app-dataset-list',
  templateUrl: './dataset-list.component.html',
  styleUrls: ['./dataset-list.component.css'],
  providers: [ConfirmationService]
})
export class DatasetListComponent implements OnInit {

  datasets: Dataset[] = [];
  loading: boolean = true;
  salvando: boolean = false;

  datasetDialog: boolean = false;
  isEditDialog: boolean = false;
  datasetEmEdicao: Dataset | null = null;

  form!: FormGroup;

  // Diálogo "Dados"
  dadosDialog: boolean = false;
  datasetDados: Dataset | null = null;
  registros: DadoDataset[] = [];
  colunas: string[] = [];
  totalRegistros: number = 0;
  carregandoDados: boolean = false;
  filtroChave: string = '';
  linhasPorPagina: number = 50;
  primeiraLinha: number = 0;

  constructor(
    private fb: FormBuilder,
    private smartintegService: SmartintegService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      nome: ['', [Validators.required, Validators.minLength(3)]],
      descricao: [''],
      camposChave: [[] as string[], [Validators.required, Validators.minLength(1)]]
    });
    this.carregarDatasets();
  }

  carregarDatasets(): void {
    this.loading = true;
    this.smartintegService.listarDatasets().subscribe({
      next: (data) => {
        this.datasets = data;
        this.loading = false;
      },
      error: (err) => {
        this.toastService.erro('Erro', 'Erro ao carregar datasets: ' + mensagemErroApi(err));
        this.loading = false;
      }
    });
  }

  // ---------- Criar / editar ----------

  novoDataset(): void {
    this.datasetEmEdicao = null;
    this.isEditDialog = false;
    this.form.reset({ nome: '', descricao: '', camposChave: [] });
    this.datasetDialog = true;
  }

  editarDataset(dataset: Dataset): void {
    this.datasetEmEdicao = dataset;
    this.isEditDialog = true;
    this.form.reset({ nome: dataset.nome, descricao: dataset.descricao ?? '', camposChave: [...(dataset.camposChave ?? [])] });
    this.datasetDialog = true;
  }

  // Campos chave são case-sensitive na carga: padroniza em maiúsculas, sem espaços e sem repetição
  normalizarChaves(): void {
    const atuais: string[] = this.form.get('camposChave')?.value ?? [];
    const normalizadas = [...new Set(atuais.map(c => c.trim().toUpperCase()).filter(c => c.length > 0))];
    if (normalizadas.length !== atuais.length || normalizadas.some((c, i) => c !== atuais[i])) {
      this.form.get('camposChave')?.setValue(normalizadas);
    }
  }

  salvarDataset(): void {
    this.normalizarChaves();
    if (this.form.invalid) {
      this.toastService.alerta('Atenção', 'Preencha todos os campos obrigatórios');
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.getRawValue();
    this.salvando = true;

    // No PUT, ausente mantém e "" limpa: a descrição é sempre enviada para refletir o que está na tela
    const acao$: Observable<unknown> = this.isEditDialog
      ? this.smartintegService.atualizarDataset({ id: this.datasetEmEdicao!.id, nome: v.nome, descricao: v.descricao ?? '', camposChave: v.camposChave })
      : this.smartintegService.criarDataset({ nome: v.nome, descricao: v.descricao || undefined, camposChave: v.camposChave });

    acao$.subscribe({
      next: () => {
        this.toastService.sucesso('Sucesso', this.isEditDialog ? 'Dataset atualizado com sucesso' : 'Dataset criado com sucesso');
        this.salvando = false;
        this.datasetDialog = false;
        this.carregarDatasets();
      },
      error: (err) => {
        this.toastService.erro('Erro', `Erro ao ${this.isEditDialog ? 'atualizar' : 'criar'} dataset: ` + mensagemErroApi(err));
        this.salvando = false;
      }
    });
  }

  campoInvalido(campo: string): boolean {
    const control = this.form.get(campo);
    return !!(control && control.invalid && control.touched);
  }

  // Ao editar, mudar as chaves não recalcula as chaves já gravadas nos registros
  get chavesAlteradas(): boolean {
    if (!this.isEditDialog || !this.datasetEmEdicao) return false;
    const antes = (this.datasetEmEdicao.camposChave ?? []).join('|');
    const depois = ((this.form.get('camposChave')?.value ?? []) as string[]).join('|');
    return antes !== depois && this.datasetEmEdicao.qtdRegistros > 0;
  }

  fecharDialog(): void {
    this.datasetDialog = false;
  }

  // ---------- Exclusão ----------

  // A exclusão é física; o backend bloqueia (400) se algum recurso ativo usa o dataset
  deletarDataset(dataset: Dataset): void {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja deletar o dataset <strong>${dataset.nome}</strong> e seus ${dataset.qtdRegistros} registro(s)? Esta ação não pode ser desfeita.`,
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      acceptLabel: 'Sim, Deletar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.loading = true;
        this.smartintegService.deletarDataset(dataset.id).subscribe({
          next: () => {
            this.toastService.sucesso('Sucesso', 'Dataset deletado com sucesso');
            this.carregarDatasets();
          },
          error: (err) => {
            this.toastService.erro('Erro', 'Erro ao deletar dataset: ' + mensagemErroApi(err));
            this.loading = false;
          }
        });
      }
    });
  }

  // ---------- Dados ----------

  verDados(dataset: Dataset): void {
    this.datasetDados = dataset;
    this.registros = [];
    this.colunas = [];
    this.totalRegistros = 0;
    this.filtroChave = '';
    this.primeiraLinha = 0;
    this.dadosDialog = true;
  }

  fecharDados(): void {
    this.dadosDialog = false;
    this.datasetDados = null;
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.linhasPorPagina = event.rows ?? this.linhasPorPagina;
    this.primeiraLinha = event.first ?? 0;
    this.carregarDados();
  }

  pesquisar(): void {
    this.primeiraLinha = 0;
    this.carregarDados();
  }

  limparFiltro(): void {
    this.filtroChave = '';
    this.pesquisar();
  }

  private carregarDados(): void {
    if (!this.datasetDados) return;
    const pagina = Math.floor(this.primeiraLinha / this.linhasPorPagina);
    this.carregandoDados = true;
    this.smartintegService
      .listarDadosDataset(this.datasetDados.id, pagina, this.linhasPorPagina, this.filtroChave.trim() || undefined)
      .subscribe({
        next: (resp) => {
          this.registros = resp.registros;
          this.totalRegistros = resp.paginacao.totalRegistros;
          this.colunas = this.colunasDe(resp.registros);
          this.carregandoDados = false;
        },
        error: (err) => {
          this.toastService.erro('Erro', 'Erro ao carregar os dados do dataset: ' + mensagemErroApi(err));
          this.carregandoDados = false;
        }
      });
  }

  // Colunas = união das chaves do jsonb "dados" da página; as campos chave vêm primeiro
  private colunasDe(registros: DadoDataset[]): string[] {
    const chaves = new Set<string>(this.datasetDados?.camposChave ?? []);
    const outras = new Set<string>();
    registros.forEach(r => Object.keys(r.dados ?? {}).forEach(k => { if (!chaves.has(k)) outras.add(k); }));
    return [...chaves, ...outras];
  }

  ehChave(coluna: string): boolean {
    return (this.datasetDados?.camposChave ?? []).includes(coluna);
  }

  valorCelula(valor: unknown): string {
    if (valor === null || valor === undefined) return '';
    return typeof valor === 'object' ? JSON.stringify(valor) : String(valor);
  }
}
